import { useEffect, useRef, useState } from 'react'
import { CHART_DEFAULT_OPTIONS, SUBTITLE_FONT_SIZE, TITLE_FONT_SIZE } from '../../common/constants'
import { convertTimestamp } from '../../common/helpers'
import { formatNumber, roundToDecimal } from '../../common/utils'
import { ChartSubHeader } from '../ChartSubHeader'
import { ChartLoadingPlaceholder } from './ChartLoadingPlaceholder'
import { CollapsibleSection } from '../CollapsibleSection'
import { SensorProduct, LINE_COLORS, DeviceInfo, UnitCategoryId } from '../../common/enums'
import { useAccountContext } from '../../contexts'
import { convertBaseToDisplay } from '../../common/conversions'
import CanvasJSReact from '@canvasjs/react-charts'
import { Viewport } from '../../common/types'
import { getUserUnitSetting } from '../../utils/unitSettings'
import { createAxisLabelFormatter, alertMarkersToStripLines, AlertMarker, getHiddenRangeStripLines } from '../../utils/chartHelpers'
const { CanvasJSChart } = CanvasJSReact

interface Props {
  device: DeviceInfo
  sensorType: SensorProduct
  sensorReadings: any[]
  viewport: Viewport
  isLoading: boolean
  zoomEnabled?: boolean
  selectXRange?: number[]
  addTimestamp?: (timestamp: number) => void
  showHiddenData?: boolean
  alertMarkers?: AlertMarker[]
}

export function UnifiedChartVegetronixTHERM200({
  device,
  sensorType,
  sensorReadings,
  viewport,
  isLoading,
  zoomEnabled = true,
  selectXRange = [],
  addTimestamp = () => {},
  showHiddenData = false,
  alertMarkers,
}: Props) {
  const { userUnitSettings, unitConversions } = useAccountContext()
  const [visible, setVisible] = useState<Record<string, boolean>>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const xRef = useRef(0)

  const unitCategoryId = UnitCategoryId.SoilTemperature

  const { unitId, precision } = getUserUnitSetting(unitCategoryId, userUnitSettings, unitConversions)
  const units = unitConversions[unitCategoryId].abbreviations[unitId]
  const formula = unitConversions[unitCategoryId].formulas[unitId]

  useEffect(() => {
    const visible: Record<string, boolean> = {}

    Object.keys(sensorReadings)
      .filter((key: string) => !key.includes('T0'))
      .map((key: string) => {
        const sensorName = sensorReadings[Number(key)].metadata.sensorName
        visible[sensorName] = true
        return null
      })

    setVisible(visible)
  }, [sensorReadings])

  const dataPoints: Record<string, any> = {}
  let yAxisMinimum = 100
  let yAxisMaximum = -100
  Object.keys(sensorReadings).forEach((key: any) => {
    const sensor = sensorReadings[key]

    const sensorName = sensor.metadata.sensorName

    // Get hidden map from sensor data for client-side filtering
    const hiddenMap = sensor.hidden || {}

    const data = Object.keys(sensor.readings)
      .filter((key: string) => !hiddenMap[key])  // Filter out hidden timestamps
      .filter((key: string) => {
        const timestamp = parseInt(key) * 1000
        return timestamp >= viewport.minimum && timestamp <= viewport.maximum
      })
      .map((key: string) => {
        const timestamp = parseInt(key)
        const reading = convertBaseToDisplay(sensor.readings[timestamp] as number, formula)

        yAxisMinimum = Math.min(yAxisMinimum, reading)
        yAxisMaximum = Math.max(yAxisMaximum, reading)

        return {
          label: convertTimestamp(timestamp),
          x: timestamp * 1000,
          y: roundToDecimal(reading, precision),
        }
      })

    dataPoints[sensorName] = data
  })

  // Build full (unfiltered) data points for ghost overlay
  const ghostDataPointsMap: Record<string, any> = {}
  const allHiddenStripLines: any[] = []
  if (showHiddenData) {
    Object.keys(sensorReadings).forEach((key: any) => {
      const sensor = sensorReadings[key]
      const sensorName = sensor.metadata.sensorName
      const hiddenMap = sensor.hidden || {}
      const ghostData = Object.keys(sensor.readings)
        .filter((k: string) => {
          const timestamp = parseInt(k) * 1000
          return timestamp >= viewport.minimum && timestamp <= viewport.maximum
        })
        .map((k: string) => {
          const timestamp = parseInt(k)
          const reading = convertBaseToDisplay(sensor.readings[timestamp] as number, formula)
          return {
            label: convertTimestamp(timestamp),
            x: timestamp * 1000,
            y: roundToDecimal(reading, precision),
          }
        })
      if (ghostData.length > 0) {
        ghostDataPointsMap[sensorName] = ghostData
      }
      allHiddenStripLines.push(...getHiddenRangeStripLines(hiddenMap))
    })
  }

  // Filter out sensors with no data points in the viewport
  const sensorsWithData = Object.keys(dataPoints).filter(
    (sensorPort) => dataPoints[sensorPort] && dataPoints[sensorPort].length > 0
  )

  const hasAnyData = sensorsWithData.length > 0

  // Register click listener ONCE when chart mounts (not on every hover)
  // Uses retry mechanism since CanvasJS renders the canvas asynchronously
  // NOTE: CanvasJS creates multiple canvas elements - we need the LAST one (topmost layer)
  useEffect(() => {
    if (!hasAnyData || !containerRef.current) return

    let canvas: HTMLCanvasElement | null = null
    let retryCount = 0
    const maxRetries = 20
    let timeoutId: ReturnType<typeof setTimeout>

    function onClickHandler() {
      addTimestamp(xRef.current)
    }

    function tryAttachListener() {
      const canvases = containerRef.current?.querySelectorAll('.canvasjs-chart-canvas')
      canvas = canvases && canvases.length > 0 ? (canvases[canvases.length - 1] as HTMLCanvasElement) : null
      if (canvas) {
        canvas.addEventListener('click', onClickHandler)
      } else if (retryCount < maxRetries) {
        retryCount++
        timeoutId = setTimeout(tryAttachListener, 100)
      }
    }

    tryAttachListener()

    return () => {
      clearTimeout(timeoutId)
      if (canvas) {
        canvas.removeEventListener('click', onClickHandler)
      }
    }
  }, [hasAnyData, addTimestamp])

  const lines = hasAnyData
    ? sensorsWithData.map((sensorPort: string) => {
        const points = dataPoints[sensorPort]
        return {
          name: `${sensorPort}`,
          type: 'line',
          showInLegend: true,
          xValueType: 'dateTime',
          color: LINE_COLORS[sensorPort],
          visible: visible[sensorPort],
          dataPoints: points,
        }
      })
    : []

  const xAxisMinimum = dataPoints[Object.keys(dataPoints)[0]]?.[0]?.x || viewport.minimum
  const xAxisMaximum =
    dataPoints[Object.keys(dataPoints)[0]]?.[dataPoints[Object.keys(dataPoints)[0]].length - 1]?.x || viewport.maximum

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_SoilTemperature`,
    title: {
      text: 'Soil Temperature',
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    toolTip: {
      shared: true,
      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`
        for (var i = 0; i < e.entries.length; i++) {
          if (e.entries[i].dataSeries.name.includes('(hidden)')) continue
          content += `<span style="color:${e.entries[i].dataSeries.color}">${
            e.entries[i].dataSeries.name
          }:</span> <strong> ${formatNumber(roundToDecimal(e.entries[i].dataPoint.y))} ${units} </strong><br/>`
        }
        xRef.current = e.entries[0].dataPoint.x
        return content
      },
    },
    axisX: {
      labelFormatter: createAxisLabelFormatter(),
      viewportMinimum: xAxisMinimum,
      viewportMaximum: xAxisMaximum,
      stripLines: [
        ...selectXRange.map((value) => ({
          value,
          color: 'red',
          labelPlacement: 'outside',
        })),
        selectXRange.length === 2 && {
          startValue: selectXRange[0],
          endValue: selectXRange[1],
          color: '#d8d8d8',
        },
      ...allHiddenStripLines,
      ...alertMarkersToStripLines(alertMarkers),
      ].filter(Boolean),
    },
    axisY: {
      includeZero: false,
      maximum: yAxisMaximum > 0 ? yAxisMaximum * 1.2 : yAxisMaximum * 0.8,
      minimum: yAxisMinimum > 0 ? yAxisMinimum * 0.8 : yAxisMinimum * 1.2,
      title: `Temperature (${units})`,
      titleFontSize: TITLE_FONT_SIZE,
      labelFontSize: TITLE_FONT_SIZE,
    },
    data: [
      ...(showHiddenData
        ? Object.keys(ghostDataPointsMap).map((sensorName) => ({
            name: `${sensorName} (hidden)`,
            type: 'line',
            showInLegend: false,
            xValueType: 'dateTime',
            lineThickness: 2,
            color: '#aaaaaa',
            markerSize: 0,
            dataPoints: ghostDataPointsMap[sensorName],
            toolTipContent: null,
          }))
        : []),
      ...lines,
    ],
    legend: {
      itemclick: (e: any) => {
        setVisible((prev) => ({ ...prev, [e.dataSeries.name]: !prev[e.dataSeries.name] }))
      },
    },
  }

  // Only show sensors with data in the ChartSubHeader
  const data = sensorsWithData.map((sensorPort: string) => {
    const readings = dataPoints[sensorPort]
    const lastIndex = readings.length - 1
    const lastTimestamp = readings[lastIndex] ? new Date(Number(readings[lastIndex]?.x)).toLocaleString() : '...'
    const lastReading = readings[lastIndex] ? formatNumber(Number(readings[lastIndex]?.y), 1) : ''

    return [`Port ${sensorPort}: ${sensorType}`, `${lastTimestamp}`, `${lastReading} ${units}`]
  })

  // If loading, show placeholder
  if (isLoading) {
    return (
      <CollapsibleSection title="Soil Temperature" isLoading={isLoading}>
        <ChartLoadingPlaceholder />
      </CollapsibleSection>
    )
  }

  // If no sensors have data in the viewport, show a "no data" message
  if (!hasAnyData) {
    // Build sub-header data for all sensors (even those without data)
    const noDataSubHeader = sensorReadings.map((sensor: any) => {
      const sensorPort = sensor.metadata.sensorName
      return [`Port ${sensorPort}: ${sensorType}`, 'No readings']
    })

    return (
      <CollapsibleSection title="Soil Temperature" isLoading={isLoading}>
        <ChartSubHeader data={noDataSubHeader} />
        <div className="flex items-center justify-center py-8">
          <div className="bg-red-500 text-white font-bold rounded px-4 py-2 mb-10 mt-6 h-10 w-[320px] h-[70px] mx-auto text-center">
            This RIC has no data for the selected period
          </div>
        </div>
      </CollapsibleSection>
    )
  }

  return (
    <CollapsibleSection title="Soil Temperature" isLoading={isLoading}>
      <ChartSubHeader data={data} />
      <div ref={containerRef}>
        <CanvasJSChart options={options} />
      </div>
    </CollapsibleSection>
  )
}
